import { Add } from "@mui/icons-material";
import { Alert, IconButton, Stack, Tooltip } from "@mui/material";
import {
  FormEvent,
  KeyboardEvent,
  useCallback,
  useMemo,
  useState,
} from "react";
import { useCreateIntakeQuestionOptionMutation } from "src/api/services/intakeQuestionOptionsApi";
import { OptionState } from "src/api/types";
import { useSentryLogger } from "src/hooks/useSentryLogger";
import LoadingButton from "../Buttons/LoadingButton";
import IntakeQuestionOptionView from "../IntakeQuestionOptionView";

const initialOptionState: OptionState[] = [
  {
    position: 0,
    questionOption: null,
    nextQuestion: null,
  },
];

interface Props {
  intakeId: number;
  intakeQuestionId?: number;
  returnToQuestion?: () => void;
}

const QuestionOptionsBuilder = ({
  intakeId,
  intakeQuestionId,
  returnToQuestion,
}: Props) => {
  const [createQuestionOption, { isLoading }] =
    useCreateIntakeQuestionOptionMutation();
  const logError = useSentryLogger("error");
  const [error, setError] = useState("");
  const [options, setOptions] = useState<OptionState[]>(initialOptionState);

  const addNewOption = useCallback(() => {
    setOptions((prev) => [
      ...prev,
      { position: prev.length, questionOption: null, nextQuestion: null },
    ]);
  }, []);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLFormElement>) => {
      if (e.metaKey && e.key === "Enter") {
        e.preventDefault();
        addNewOption();
      }
    },
    [addNewOption],
  );

  const body = useMemo(() => {
    return options
      .filter((opt) => opt.questionOption != null)
      .map((opt) => ({
        option_id: opt.questionOption!.id,
        sort_order: opt.position,
        next_question: opt.nextQuestion?.slug,
      }));
  }, [options]);

  const handleSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      setError("");

      if (options.some((opt) => opt.questionOption == null)) {
        setError("Please select a question option for all options");
        return;
      }

      if (intakeQuestionId == null) {
        setError("Question is missing, please make sure a question was added");
        return;
      }

      try {
        await createQuestionOption({
          intakeId,
          questionId: intakeQuestionId,
          body,
        });

        setOptions(initialOptionState);
        returnToQuestion?.();
      } catch (e) {
        logError("Failed to create intake question", e);
        setError("Failed to create intake question");
      }
    },
    [
      body,
      createQuestionOption,
      intakeId,
      intakeQuestionId,
      logError,
      options,
      returnToQuestion,
    ],
  );

  return (
    <Stack
      gap={2}
      component="form"
      onSubmit={handleSubmit}
      onKeyDown={handleKeyDown}
      aria-label="question-options-builder"
    >
      <Stack gap={2}>
        {options.map((option, index) => (
          <IntakeQuestionOptionView
            key={index}
            index={index}
            option={option}
            setOption={setOptions}
            setError={setError}
          />
        ))}
        <Tooltip title="Add another question option">
          <IconButton
            onClick={addNewOption}
            color="primary"
            sx={{ alignSelf: "flex-end" }}
          >
            <Add />
          </IconButton>
        </Tooltip>

        <LoadingButton type="submit" loading={isLoading}>
          Create options and continue
        </LoadingButton>
      </Stack>
      {error ? <Alert severity={"error"}>{error}</Alert> : null}
    </Stack>
  );
};

export default QuestionOptionsBuilder;
